import { Assumptions, CompModel, Scenario } from '../types/compensation'

export const defaultAssumptions: Assumptions = {
  referenceMonthlySalary: 100_000,
  minFixedSalary: 50_000,
  maxFixedSalary: 80_000,
  commissionMultiplier: 3,
  equityMultiplier: 5,
  existingRateMin: 5,
  existingRateMax: 10,
  newRateMin: 10,
  newRateMax: 18,
  existingRevenueTarget: 5_000_000,
  newBusinessTarget: 7_500_000,
  companyValuation: 60_000_000,
  vestingYears: 4,
  cliffMonths: 12,
  firstYearVestPercent: 25,
  performanceWarrants: false,
  milestones: [
    { id: 'm1', label: 'First DKK 5m strategic account', grant: '' },
    { id: 'm2', label: 'First DKK 10m annual Pharma account', grant: '' },
    { id: 'm3', label: 'First major new-logo global agreement', grant: '' },
  ],
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max)

/**
 * The single economic pool: every krone of salary sacrifice is allocated
 * EITHER to commission OR to equity — never to both.
 */
export function computeModel(
  fixedMonthly: number,
  commissionShare: number,
  a: Assumptions,
): CompModel {
  const monthly = clamp(fixedMonthly, a.minFixedSalary, a.maxFixedSalary)
  const share = clamp(commissionShare, 0, 1)
  const equityShare = 1 - share

  const monthlySacrifice = Math.max(a.referenceMonthlySalary - monthly, 0)
  const annualSacrifice = monthlySacrifice * 12

  const commissionCash = annualSacrifice * share
  const equityCash = annualSacrifice * equityShare

  const commissionOpportunity = commissionCash * a.commissionMultiplier
  const equityValue = equityCash * a.equityMultiplier

  const existingRate =
    a.existingRateMin + share * (a.existingRateMax - a.existingRateMin)
  const newBusinessRate =
    a.newRateMin + share * (a.newRateMax - a.newRateMin)

  const existingCommission = a.existingRevenueTarget * (existingRate / 100)
  const newBusinessCommission = a.newBusinessTarget * (newBusinessRate / 100)
  const expectedCommission = existingCommission + newBusinessCommission

  const fixedAnnual = monthly * 12
  const cashOte = fixedAnnual + expectedCommission

  const scenarioDefs: Array<{ id: string; label: string; attainment: number }> =
    [
      { id: 'low', label: 'Low performance', attainment: 0.5 },
      { id: 'target', label: 'At target', attainment: 1 },
      { id: 'strong', label: 'Strong performance', attainment: 1.5 },
    ]

  const scenarios: Scenario[] = scenarioDefs.map((s) => {
    const commission = expectedCommission * s.attainment
    return {
      ...s,
      commission,
      cash: fixedAnnual + commission,
    }
  })

  return {
    fixedMonthly: monthly,
    fixedAnnual,
    monthlySacrifice,
    annualSacrifice,
    commissionShare: share,
    equityShare,
    commissionCash,
    equityCash,
    commissionOpportunity,
    equityValue,
    ownershipPercent:
      a.companyValuation > 0 ? (equityValue / a.companyValuation) * 100 : 0,
    existingRate,
    newBusinessRate,
    existingCommission,
    newBusinessCommission,
    expectedCommission,
    cashOte,
    scenarios,
  }
}

export function vestingSummary(a: Assumptions): string {
  const remaining = a.vestingYears * 12 - a.cliffMonths
  return `${a.vestingYears}-year vesting · ${a.cliffMonths}-month cliff · ${a.firstYearVestPercent}% at cliff, then monthly over ${remaining} months`
}
